import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import {
  UsersIcon,
  MapPinIcon,
  PhoneIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import HomeNav from '../navigations/homenav';
import Footer from '../navigations/Footer';
import { useLanguageContext } from '../navigations/homenav';

// Testimonial Card Component
const TestimonialCard = ({ quote, name, role, image, rating }) => {
  const stars = [];
  for (let i = 0; i < 5; i++) {
    stars.push(i < rating ? <StarIconSolid key={i} className="h-5 w-5 text-yellow-400" /> : <StarIconSolid key={i} className="h-5 w-5 text-gray-300" />);
  }

  return (
    <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 text-center">
      <div className="mb-6">
        <img className="w-20 h-20 rounded-full object-cover mx-auto" src={image} alt={name} />
      </div>
      <p className="text-gray-600 italic mb-6">"{quote}"</p>
      <div className="flex justify-center mb-4">{stars}</div>
      <div>
        <p className="font-semibold text-gray-800">{name}</p>
        <p className="text-sm text-gray-500">{role}</p>
      </div>
    </div>
  );
};

// Member Organization Card Component
const MemberOrgCard = ({ name, logo, description }) => (
  <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 text-center">
    <img className="h-16 w-auto object-contain mx-auto mb-4" src={logo} alt={name} />
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{name}</h3>
    <p className="text-sm text-gray-600 mb-2">{description}</p>
  </div>
);

// Benefit Card Component
const BenefitCard = ({ icon, title, description }) => (
  <div className="bg-gray-50 p-6 rounded-lg hover:bg-gray-100 transition-colors duration-300">
    <div className="flex items-center mb-4">
      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-sky-100 text-sky-600 mr-4">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
    </div>
    <p className="text-gray-600 text-sm">{description}</p>
  </div>
);

const PublicMembersPage = () => {
  const { t } = useLanguageContext();
  const mapRef = useRef(null);
  

  useEffect(() => {
    // Map removed: no initialization needed
  }, []);
  

  const testimonials = [
    {
      quote: "Joining this community has been life-changing. The support and resources are incredible, and I've connected with so many amazing people.",
      name: "Abebe Kebede",
      role: "Member & Advocate",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=60",
      rating: 5
    },
    {
      quote: "The vocational training program helped me secure a job that I love. I'm now financially independent and more confident than ever.",
      name: "Fatuma Ahmed",
      role: "Program Graduate",
      image: "https://images.unsplash.com/photo-1539571696357-5973dc0f98f6?auto=format&fit=crop&w=200&q=60",
      rating: 5
    },
    {
      quote: "As a parent, knowing my child has access to quality education and a supportive network gives me immense peace of mind. This organization is a blessing.",
      name: "Chaltu Negash",
      role: "Parent of a Member",
      image: "https://images.unsplash.com/photo-1521119989659-e0d53f98f2ca?auto=format&fit=crop&w=200&q=60",
      rating: 5
    }
  ];

  const memberOrgs = [
    { name: "ZOA", logo: "/zoalogo.png"},
    { name: "We Are Able", logo: "/we-are-able.png"},
    { name: "DPO networks", logo: "dpologo.png"},
    { name: "International Partners", logo: "/internationalpartners.png"},
    { name: "Civil Society Organizations (CSOs)", logo: "/civil-society-organization-logo.png"},
    { name: "Government Offices", logo: "/governmentlogo.png"},
  ];

  const benefits = [
    {
      icon: <UsersIcon className="h-6 w-6" />,
      titleKey: 'publicMembersBenefit1Title',
      descKey: 'publicMembersBenefit1Desc',
    },
    {
      icon: <CheckBadgeIcon className="h-6 w-6" />,
      titleKey: 'publicMembersBenefit2Title',
      descKey: 'publicMembersBenefit2Desc',
    },
    {
      icon: <UsersIcon className="h-6 w-6" />,
      titleKey: 'publicMembersBenefit3Title',
      descKey: 'publicMembersBenefit3Desc',
    },
    {
      icon: <CheckBadgeIcon className="h-6 w-6" />,
      titleKey: 'publicMembersBenefit4Title',
      descKey: 'publicMembersBenefit4Desc',
    }
  ];

  return (
    <div className="bg-gray-50 text-gray-800">
      <HomeNav />

      <main>
        {/* Hero Section */}
        <section
          className="py-24 bg-center text-white relative"
          style={{
            backgroundImage: "url('/homeimage8.png')",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            minHeight: "400px"
          }}
        >
          <div className="absolute inset-0 bg-slate-800 bg-opacity-60"></div>
          <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
            <UsersIcon className="h-16 w-16 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">
              {t('partnersHeading')}
            </h1>
            <p className="mt-4 text-lg md:text-xl text-sky-100 max-w-3xl mx-auto">
              {t('publicMembersHeroSubtitle')}
            </p>
          </div>
        </section>

        {/* Member Organizations Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('publicMembersPartnersTitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t('publicMembersPartnersDesc')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {memberOrgs.map((org, index) => (
                <MemberOrgCard key={index} {...org} />
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <p className="text-sm font-semibold text-sky-600 uppercase tracking-wider">{t('publicMembersWhyJoin')}</p>
              <h2 className="mt-2 text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('publicMembersBenefitsTitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t('publicMembersBenefitsDesc')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <BenefitCard
                  key={index}
                  icon={benefit.icon}
                  title={t(benefit.titleKey)}
                  description={t(benefit.descKey)}
                />
              ))}
            </div>
          </div>
        </section>

        

        {/* Call to Action Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <UsersIcon className="h-16 w-16 mx-auto text-sky-600 mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('publicMembersCtaTitle')}
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              {t('publicMembersCtaDesc')}
            </p>
            <div className="flex justify-center">
              <Link
                to="/contactus"
                className="inline-block px-8 py-3 rounded-md text-lg font-medium bg-sky-600 text-white hover:bg-sky-700 transition-colors duration-300"
              >
                {t('contactUs')}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PublicMembersPage;
